import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFetch } from '../hooks/useFetch.js'
import { getActivities } from '../utils/api.js'
import { getActivityEmoji } from '../utils/telegram.js'
import UpdatedLabel from '../components/UpdatedLabel.jsx'

function normalizeActivityType(type) {
  return String(type || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
}

function sanitizeVenueName(name) {
  const normalized = String(name || '').trim()
  if (!normalized) return ''
  return normalized.toLowerCase() === 'no brand' ? '' : normalized
}

function SlotsIndicator({ available, total, mode = 'ratio' }) {
  if (mode === 'totalOnly') {
    if (total === null || total === undefined) return null
    return <span className="event-slots">{total} slots</span>
  }
  if (available === null || available === undefined) return null
  const pct = total ? available / total : 1
  const cls = available === 0 ? 'none' : pct < 0.2 ? 'low' : ''
  return (
    <span className={`event-slots ${cls}`}>
      {available}/{total ?? '?'}
    </span>
  )
}

function BrandItem({ brandId, brandInfo, activityType, fetchedAt }) {
  const navigate = useNavigate()
  const typeKey = normalizeActivityType(activityType)
  const isIceRink = typeKey === 'ice_rink'
  const isQuiz = typeKey === 'quiz' || typeKey === 'quiz_night'
  const totalSlots = brandInfo.events.reduce((s, e) => s + (e.totalSlots || 0), 0)
  const availSlots = brandInfo.events.reduce((s, e) => s + (e.availableSlots || 0), 0)
  const upcomingCount = brandInfo.events.length
  const venueName = sanitizeVenueName(brandInfo.venueName)
  const showBrandName = Boolean(venueName)

  return (
    <div
      className="venue-item"
      onClick={() => navigate(`/venue/${encodeURIComponent(brandId)}`, {
        state: { venueName, activityType }
      })}
    >
      <div className="venue-info">
        {showBrandName && <h4>{venueName}</h4>}
        {brandInfo.address && <p>📍 {brandInfo.address}</p>}
        <p>
          {upcomingCount} upcoming event{upcomingCount !== 1 ? 's' : ''}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        {!isQuiz && (
          <SlotsIndicator
            available={availSlots}
            total={totalSlots || null}
            mode={isIceRink ? 'totalOnly' : 'ratio'}
          />
        )}
        <span style={{ color: 'var(--ds-text-secondary)', fontSize: 16 }}>›</span>
        <UpdatedLabel timestamp={fetchedAt} />
      </div>
    </div>
  )
}

function ActivityGroup({ activityType, venues, fetchedAt }) {
  const [expanded, setExpanded] = useState(false)
  const brandEntries = Object.entries(venues)
  const emoji = getActivityEmoji(activityType)
  const totalEvents = brandEntries.reduce((s, [, b]) => s + b.events.length, 0)

  return (
    <div className="type-group">
      <div
        className={`type-header ${expanded ? 'expanded' : ''}`}
        onClick={() => setExpanded(e => !e)}
      >
        <h3>{emoji} {activityType.replace(/_/g, ' ')}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
            {brandEntries.length} brand{brandEntries.length !== 1 ? 's' : ''} · {totalEvents} event{totalEvents !== 1 ? 's' : ''}
          </span>
          <span className="chevron">▾</span>
        </div>
      </div>

      {expanded && brandEntries.map(([brandId, brandInfo]) => (
        <BrandItem
          key={brandId}
          brandId={brandId}
          brandInfo={brandInfo}
          activityType={activityType}
          fetchedAt={fetchedAt}
        />
      ))}
    </div>
  )
}

export default function AllPage() {
  const { data, loading, showLoader, error, refetch } = useFetch(() => getActivities())

  // Initial load with no cached data: show spinner only after the delay threshold
  if (!data && !error) {
    if (!showLoader) return null
    return (
      <div className="status-message">
        <div className="loading-spinner" />
        <p style={{ marginTop: 12 }}>Loading events…</p>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="status-message">
        <p>⚠️ {error}</p>
        <button
          className="btn btn-secondary btn-sm"
          style={{ marginTop: 12, width: 'auto' }}
          onClick={refetch}
        >
          Retry
        </button>
      </div>
    )
  }

  const grouped = data?.data || {}
  const fetchedAt = data?.fetchedAt || null
  const types = Object.keys(grouped)

  if (types.length === 0 && !loading) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🎯</div>
        <div className="empty-state-title">No events available</div>
        <div className="empty-state-text">Check back later for upcoming events</div>
      </div>
    )
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-title">
          All Events
          {showLoader && <span className="inline-loader" aria-label="Refreshing" />}
        </div>
        <div className="page-header-subtitle">
          {data?.count} event{data?.count !== 1 ? 's' : ''} · {types.length} categor{types.length !== 1 ? 'ies' : 'y'}
        </div>
      </div>

      {types.map(type => (
        <ActivityGroup key={type} activityType={type} venues={grouped[type]} fetchedAt={fetchedAt} />
      ))}

      <button
        className="btn btn-secondary"
        style={{ marginTop: 8 }}
        onClick={refetch}
        disabled={loading}
      >
        ↻ Refresh
      </button>
    </div>
  )
}
